import React from 'react';
import { useQuery } from 'react-query';
import { getConversation } from '../services/conversations';
import ConversationCard from './ConversationCard';

interface ConversationDetailProps {
  conversationId: string;
  onBack: () => void;
}

const ConversationDetail: React.FC<ConversationDetailProps> = ({ conversationId, onBack }) => {
  const { data: conversation, isLoading, error } = useQuery(
    ['conversation', conversationId],
    () => getConversation(conversationId),
    {
      enabled: !!conversationId,
    }
  );

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#000000' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="animate-spin rounded-full h-12 w-12 mx-auto mb-4" style={{ 
            borderWidth: '2px', 
            borderStyle: 'solid', 
            borderColor: '#ffffff transparent transparent transparent' 
          }}></div>
          <p style={{ 
            color: '#ffffff', 
            fontFamily: "'Inter', sans-serif" 
          }}>
            Loading conversation...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#000000' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⚠️</div>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#ffffff',
            marginBottom: '0.5rem',
            fontFamily: "'Inter', sans-serif",
            letterSpacing: '0.05em'
          }}>
            ERROR LOADING CONVERSATION
          </h2>
          <p style={{
            color: '#ffffff',
            marginBottom: '1.5rem',
            fontFamily: "'Inter', sans-serif"
          }}>
            There was an error loading the conversation details.
          </p>
          <button
            onClick={onBack}
            style={{
              background: 'transparent',
              border: '2px solid #ffffff',
              color: '#ffffff',
              padding: '0.75rem 2rem',
              fontSize: '0.9rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontFamily: "'Inter', sans-serif",
              borderRadius: 0
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#ffffff';
              e.currentTarget.style.color = '#000000';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#ffffff';
            }}
          >
            Back to Conversations
          </button>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#000000' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📄</div>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#ffffff',
            marginBottom: '0.5rem',
            fontFamily: "'Inter', sans-serif",
            letterSpacing: '0.05em'
          }}>
            CONVERSATION NOT FOUND
          </h2>
          <p style={{
            color: '#ffffff',
            marginBottom: '1.5rem',
            fontFamily: "'Inter', sans-serif"
          }}>
            The requested conversation could not be found.
          </p>
          <button
            onClick={onBack}
            style={{
              background: 'transparent',
              border: '2px solid #ffffff',
              color: '#ffffff',
              padding: '0.75rem 2rem',
              fontSize: '0.9rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontFamily: "'Inter', sans-serif",
              borderRadius: 0
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#ffffff';
              e.currentTarget.style.color = '#000000';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#ffffff';
            }}
          >
            Back to Conversations
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#000000' }}>
      {/* Header */}
      <div style={{
        backgroundColor: '#000000',
        borderBottom: '1px solid rgba(245, 245, 220, 0.1)',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={onBack}
                style={{
                  color: '#ffffff',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#ffffff'}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#ffffff',
                fontFamily: "'Inter', sans-serif",
                letterSpacing: '0.05em'
              }}>
                {conversation.title}
              </h1>
            </div>
            <div style={{
              fontSize: '0.875rem',
              color: '#ffffff',
              fontFamily: "'Inter', sans-serif",
              letterSpacing: '0.05em'
            }}>
              {conversation.messages?.length || 0} MESSAGES
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Conversation Card with Analysis */}
          <ConversationCard 
            conversation={conversation} 
            onViewDetails={() => {}} // Not needed in detail view
          />
          
          {/* Messages */}
          <div style={{
            marginTop: '2rem',
            backgroundColor: 'rgba(245, 245, 220, 0.05)',
            border: '1px solid rgba(245, 245, 220, 0.2)',
            borderRadius: 0,
            padding: '1.5rem'
          }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              color: '#ffffff',
              marginBottom: '1rem',
              fontFamily: "'Inter', sans-serif",
              letterSpacing: '0.05em'
            }}>
              Conversation Messages
            </h3>
            <div className="space-y-4">
              {conversation.messages?.map((message, index) => (
                <div
                  key={index}
                  style={{
                    padding: '1rem',
                    borderRadius: 0,
                    backgroundColor: message.role === 'user' 
                      ? 'rgba(222, 184, 135, 0.1)' 
                      : message.role === 'assistant'
                      ? 'rgba(245, 245, 220, 0.05)'
                      : 'rgba(230, 126, 34, 0.1)',
                    borderLeft: message.role === 'user'
                      ? '3px solid #ffffff'
                      : message.role === 'assistant'
                      ? '3px solid rgba(245, 245, 220, 0.3)'
                      : '3px solid #e67e22'
                  }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div style={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: message.role === 'user' ? '#ffffff' : '#ffffff',
                      fontFamily: "'Inter', sans-serif",
                      letterSpacing: '0.05em'
                    }}>
                      {message.role.toUpperCase()}
                    </div>
                    {message.timestamp && (
                      <div style={{
                        fontSize: '0.75rem',
                        color: 'rgba(210, 180, 140, 0.6)',
                        fontFamily: "'Inter', sans-serif"
                      }}>
                        {new Date(message.timestamp).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div style={{
                    color: '#ffffff',
                    whiteSpace: 'pre-wrap',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: '0.875rem',
                    lineHeight: '1.6'
                  }}>
                    {message.content}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationDetail;
